import { lazy, Suspense, useState, useEffect } from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import { Helmet, HelmetProvider } from "react-helmet-async"
import { supabase } from "../../backend/supabaseClient"
import ScrollToTop from "./components/ScrollToTop"

// ── Chargement immédiat (pages principales) ──
import LandingPage          from "./pages/LandingPage"
import DevisPage            from "./pages/DevisPage"
import About                from "./pages/About"
import Services             from "./pages/Services"
import Contact              from "./pages/Contact"
import Carriere             from "./pages/Carriere"
import ClientForm           from "./pages/devis/ClientForm"

// ── Lazy loading (pages secondaires + admin) ──
const Login                 = lazy(() => import("./pages/Login"))
const AdminDashboard        = lazy(() => import("./components/AdminDashboard"))
const ConventionDashboard   = lazy(() => import("./components/ConventionDashboard"))
const EtudeVRD              = lazy(() => import("./pages/devis/EtudeVRD"))
const OPC                   = lazy(() => import("./pages/devis/OPC"))
const Noticencendie         = lazy(() => import("./pages/devis/Noticencendie"))
const NoteCalculs           = lazy(() => import("./pages/devis/NoteCalculs"))
const ExpertiseTechnique    = lazy(() => import("./pages/devis/ExpertiseTechnique"))
const EfficaciteEnergetique = lazy(() => import("./pages/devis/EfficaciteEnergetique"))
const SuiviControle         = lazy(() => import("./pages/devis/SuiviControle"))
const Cps                   = lazy(() => import("./pages/devis/Cps"))
const ConsultationTechnique = lazy(() => import("./pages/devis/ConsultationTechnique"))

// ── Composant de titre SEO par route ──
function PageMeta({ title, description }) {
  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
    </Helmet>
  )
}

const SEO = {
  home: {
    title: "ARCHENGYTECH – Bureau d'études techniques & ingénierie",
    description: "ARCHENGYTECH, bureau d'études spécialisé en ingénierie, architecture et solutions techniques. Expertise, innovation et qualité au service de vos projets.",
  },
  about: {
    title: "À propos – ARCHENGYTECH | Bureau d'études Marrakech",
    description: "Découvrez ARCHENGYTECH, bureau d'ingénierie spécialisé dans les études techniques, la conception et le suivi de projets de construction à Marrakech.",
  },
  services: {
    title: "Nos Services – ARCHENGYTECH | Ingénierie & Architecture",
    description: "Études structure, VRD, OPC, sécurité incendie, efficacité énergétique et plus. Découvrez tous les services d'ARCHENGYTECH.",
  },
  contact: {
    title: "Contact – ARCHENGYTECH | Marrakech",
    description: "Contactez ARCHENGYTECH à Marrakech. Tél : +212 6 62 25 78 79. Email : contact@archengytech.ma. Réponse sous 24h.",
  },
  carriere: {
    title: "Carrière – ARCHENGYTECH | Rejoignez notre équipe",
    description: "Rejoignez l'équipe ARCHENGYTECH. Consultez nos offres d'emploi en ingénierie, architecture et études techniques à Marrakech.",
  },
  devis: {
    title: "Demande de Devis – ARCHENGYTECH",
    description: "Demandez un devis pour vos études techniques : structure, VRD, OPC, sécurité incendie, efficacité énergétique et plus.",
  },
  devisEtudeStructure: {
    title: "Devis Étude de Structure – ARCHENGYTECH",
    description: "Demandez un devis pour une étude de structure avec ARCHENGYTECH, bureau d'études techniques à Marrakech.",
  },
  devisEtudeVrd: {
    title: "Devis Étude VRD – ARCHENGYTECH",
    description: "Demandez un devis pour une étude VRD (Voirie et Réseaux Divers) avec ARCHENGYTECH.",
  },
  devisOpc: {
    title: "Devis OPC – ARCHENGYTECH",
    description: "Demandez un devis pour une mission OPC (Ordonnancement, Pilotage et Coordination) avec ARCHENGYTECH.",
  },
  devisNoticeIncendie: {
    title: "Devis Notice Sécurité Incendie – ARCHENGYTECH",
    description: "Demandez un devis pour une notice de sécurité incendie avec ARCHENGYTECH, experts en ingénierie à Marrakech.",
  },
  devisNoteCalculs: {
    title: "Devis Note de Calculs – ARCHENGYTECH",
    description: "Demandez un devis pour une note de calculs structuraux avec ARCHENGYTECH.",
  },
  devisExpertise: {
    title: "Devis Expertise Technique – ARCHENGYTECH",
    description: "Demandez un devis pour une expertise technique de bâtiment avec ARCHENGYTECH à Marrakech.",
  },
  devisEnergie: {
    title: "Devis Efficacité Énergétique – ARCHENGYTECH",
    description: "Demandez un devis pour une étude d'efficacité énergétique avec ARCHENGYTECH.",
  },
  devisSuivi: {
    title: "Devis Suivi & Contrôle Technique – ARCHENGYTECH",
    description: "Demandez un devis pour le suivi et contrôle technique de votre projet avec ARCHENGYTECH.",
  },
  devisCps: {
    title: "Devis CPS & Métrés – ARCHENGYTECH",
    description: "Demandez un devis pour un cahier des prescriptions spéciales et estimation des métrés avec ARCHENGYTECH.",
  },
  devisConsultation: {
    title: "Devis Consultation Technique – ARCHENGYTECH",
    description: "Demandez un devis pour une consultation technique avec ARCHENGYTECH, bureau d'études à Marrakech.",
  },
}

// status: 'loading' | 'ok' | 'denied'
function RoleRoute({ children, allow }) {
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let mounted = true;

    async function checkAccess() {
      try {
        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession();

        if (sessionError || !session) {
          if (mounted) setStatus('denied');
          return;
        }

        const { data: profile, error: profileError } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', session.user.id)
          .single();

        if (!mounted) return;

        if (
          profileError ||
          !profile ||
          !allow.includes(profile.role)
        ) {
          setStatus('denied');
          return;
        }

        setStatus('ok');

      } catch (error) {
        console.error("Erreur vérification accès :", error);

        if (mounted) {
          setStatus('denied');
        }
      }
    }

    checkAccess();

    return () => {
      mounted = false;
    };
  }, [allow]);

  if (status === 'loading') {
    return null;
  }

  if (status === 'denied') {
    return <Navigate to="/login" replace />;
  }

  return children;
}
export default function App() {
  return (
    <HelmetProvider>
      <ScrollToTop />
      <Suspense fallback={null}>
        <Routes>
          <Route path="/" element={
            <><PageMeta {...SEO.home} /><LandingPage /></>
          } />
          <Route path="/about" element={
            <><PageMeta {...SEO.about} /><About /></>
          } />
          <Route path="/services" element={
            <><PageMeta {...SEO.services} /><Services /></>
          } />
          <Route path="/contact" element={
            <><PageMeta {...SEO.contact} /><Contact /></>
          } />
          <Route path="/carriere" element={
            <><PageMeta {...SEO.carriere} /><Carriere /></>
          } />
          <Route path="/devis" element={
            <><PageMeta {...SEO.devis} /><DevisPage /></>
          } />

          <Route path="/login" element={<Login />} />

          <Route path="/admin" element={
            <RoleRoute allow={['admin']}><AdminDashboard /></RoleRoute>
          } />

          <Route path="/conventions" element={
            <RoleRoute allow={['admin', 'convention']}><ConventionDashboard /></RoleRoute>
          } />

          <Route path="/devis/etude-structure" element={
            <><PageMeta {...SEO.devisEtudeStructure} /><ClientForm /></>
          } />
          <Route path="/devis/etude-vrd" element={
            <><PageMeta {...SEO.devisEtudeVrd} /><EtudeVRD /></>
          } />
          <Route path="/devis/opc" element={
            <><PageMeta {...SEO.devisOpc} /><OPC /></>
          } />
          <Route path="/devis/notice-securite-incendie" element={
            <><PageMeta {...SEO.devisNoticeIncendie} /><Noticencendie /></>
          } />
          <Route path="/devis/note-calculs" element={
            <><PageMeta {...SEO.devisNoteCalculs} /><NoteCalculs /></>
          } />
          <Route path="/devis/expertise-technique" element={
            <><PageMeta {...SEO.devisExpertise} /><ExpertiseTechnique /></>
          } />
          <Route path="/devis/efficacite-energetique" element={
            <><PageMeta {...SEO.devisEnergie} /><EfficaciteEnergetique /></>
          } />
          <Route path="/devis/suivi-controle-technique" element={
            <><PageMeta {...SEO.devisSuivi} /><SuiviControle /></>
          } />
          <Route path="/devis/cps-metres-estimation" element={
            <><PageMeta {...SEO.devisCps} /><Cps /></>
          } />
          <Route path="/devis/consultation-technique" element={
            <><PageMeta {...SEO.devisConsultation} /><ConsultationTechnique /></>
          } />
        </Routes>
      </Suspense>
    </HelmetProvider>
  )
}